import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { format } from 'date-fns'
import {
  MessageSquare, Search, User, Bot, ChevronRight, ChevronLeft,
  Car, Clock, Wrench, ArrowLeft, RefreshCw, Filter, Send, Pause, Play
} from 'lucide-react'

export default function ConciergeLogs() {
  const { user, profile } = useAuth()
  const [sessions, setSessions] = useState([])
  const [selectedSession, setSelectedSession] = useState(null)
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [search, setSearch] = useState('')
  const [profiles, setProfiles] = useState({})
  const [replyText, setReplyText] = useState('')
  const [sendingReply, setSendingReply] = useState(false)
  const [replyError, setReplyError] = useState('')
  const [updatingMode, setUpdatingMode] = useState(false)

  useEffect(() => {
    loadSessions()
  }, [])

  const loadSessions = async () => {
    setLoading(true)
    try {
      // Fetch all AI chat sessions
      const { data: sessionsData, error } = await supabase
        .from('ai_chat_sessions')
        .select(`
          *,
          vehicle:vehicles(id, year, make, model)
        `)
        .order('updated_at', { ascending: false })
        .limit(100)

      if (error) throw error

      setSessions(sessionsData || [])

      // Fetch profiles for all client_ids
      const clientIds = [...new Set((sessionsData || []).map(s => s.client_id).filter(Boolean))]
      if (clientIds.length > 0) {
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('id, full_name, email')
          .in('id', clientIds)
        if (profilesData) {
          const map = {}
          profilesData.forEach(p => { map[p.id] = p })
          setProfiles(map)
        }
      }
    } catch (err) {
      console.error('Error loading sessions:', err)
    } finally {
      setLoading(false)
    }
  }

  const loadMessages = async (session) => {
    setSelectedSession(session)
    setLoadingMessages(true)
    try {
      const { data, error } = await supabase
        .from('ai_chat_messages')
        .select('*')
        .eq('session_id', session.id)
        .order('created_at', { ascending: true })

      if (error) throw error
      setMessages(data || [])
    } catch (err) {
      console.error('Error loading messages:', err)
    } finally {
      setLoadingMessages(false)
    }
  }

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedSession || !user) return

    setSendingReply(true)
    setReplyError('')

    try {
      const response = await fetch('/api/concierge/admin-reply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: selectedSession.id,
          content: replyText.trim(),
          adminId: user.id,
          adminName: profile?.full_name || 'RDC Team'
        })
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to send reply')
      }

      // Reload messages to show the new reply
      await loadMessages(selectedSession)
      setReplyText('')
    } catch (err) {
      console.error('Error sending reply:', err)
      setReplyError(err.message || 'Failed to send reply')
    } finally {
      setSendingReply(false)
    }
  }

  const handleChangeMode = async (newMode) => {
    if (!selectedSession) return

    setUpdatingMode(true)
    try {
      const { error } = await supabase
        .from('ai_chat_sessions')
        .update({ mode: newMode })
        .eq('id', selectedSession.id)

      if (error) throw error

      // Update local session state
      setSelectedSession({ ...selectedSession, mode: newMode })
      setSessions(sessions.map(s => s.id === selectedSession.id ? { ...s, mode: newMode } : s))
    } catch (err) {
      console.error('Error updating session mode:', err)
      alert('Failed to update session mode')
    } finally {
      setUpdatingMode(false)
    }
  }

  const filteredSessions = sessions.filter(session => {
    if (!search) return true
    const q = search.toLowerCase()
    const client = profiles[session.client_id]
    const clientName = client?.full_name?.toLowerCase() || ''
    const clientEmail = client?.email?.toLowerCase() || ''
    const title = session.title?.toLowerCase() || ''
    const vehicle = session.vehicle
      ? `${session.vehicle.year} ${session.vehicle.make} ${session.vehicle.model}`.toLowerCase()
      : ''
    return clientName.includes(q) || clientEmail.includes(q) || title.includes(q) || vehicle.includes(q)
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-rdc-forest border-t-transparent" />
      </div>
    )
  }

  // Message detail view
  if (selectedSession) {
    const client = profiles[selectedSession.client_id]

    return (
      <div className="animate-fade-in">
        <button
          onClick={() => { setSelectedSession(null); setMessages([]) }}
          className="inline-flex items-center gap-2 text-rdc-taupe hover:text-rdc-primary mb-6"
        >
          <ArrowLeft size={20} />
          Back to Sessions
        </button>

        {/* Session Header */}
        <div className="card p-5 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="section-title text-lg">
                {selectedSession.title || 'Untitled Session'}
              </h2>
              <div className="flex flex-wrap gap-4 mt-2 text-sm text-rdc-taupe">
                <span className="flex items-center gap-1.5">
                  <User size={14} />
                  {client?.full_name || 'Unknown Client'}
                  {client?.email && (
                    <span className="text-xs text-rdc-warm-gray">({client.email})</span>
                  )}
                </span>
                {selectedSession.vehicle && (
                  <span className="flex items-center gap-1.5">
                    <Car size={14} />
                    {selectedSession.vehicle.year} {selectedSession.vehicle.make} {selectedSession.vehicle.model}
                  </span>
                )}
                <span className="flex items-center gap-1.5">
                  <Clock size={14} />
                  {format(new Date(selectedSession.created_at), 'MMM d, yyyy h:mm a')}
                </span>
              </div>
            </div>
            <span className="text-xs text-rdc-taupe bg-rdc-cream px-3 py-1 rounded-full">
              {messages.length} messages
            </span>
          </div>

          {/* Mode Controls */}
          <div className="flex items-center gap-3 pt-4 border-t border-rdc-warm-gray/20">
            <span className="text-xs font-semibold text-rdc-taupe uppercase tracking-wide">Mode:</span>
            <div className="flex gap-2">
              <button
                onClick={() => handleChangeMode('ai')}
                disabled={updatingMode || selectedSession.mode === 'ai'}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  selectedSession.mode === 'ai'
                    ? 'bg-rdc-primary text-white'
                    : 'bg-rdc-cream text-rdc-taupe hover:bg-rdc-primary/10'
                } disabled:opacity-50`}
              >
                AI
              </button>
              <button
                onClick={() => handleChangeMode('human')}
                disabled={updatingMode || selectedSession.mode === 'human'}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  selectedSession.mode === 'human'
                    ? 'bg-rdc-forest text-white'
                    : 'bg-rdc-cream text-rdc-taupe hover:bg-rdc-forest/10'
                } disabled:opacity-50`}
              >
                Human
              </button>
              <button
                onClick={() => handleChangeMode('paused')}
                disabled={updatingMode || selectedSession.mode === 'paused'}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center gap-1 ${
                  selectedSession.mode === 'paused'
                    ? 'bg-amber-600 text-white'
                    : 'bg-rdc-cream text-rdc-taupe hover:bg-amber-100'
                } disabled:opacity-50`}
              >
                <Pause size={14} />
                Paused
              </button>
            </div>
          </div>
        </div>

        {/* Messages */}
        {loadingMessages ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-rdc-forest border-t-transparent" />
          </div>
        ) : messages.length === 0 ? (
          <div className="card p-12 text-center">
            <MessageSquare size={48} className="text-rdc-warm-gray mx-auto mb-4" />
            <h3 className="font-display text-lg font-semibold text-black mb-2">No messages</h3>
            <p className="text-rdc-taupe">This session has no messages yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((msg) => {
              const isAdminMessage = msg.admin_id && msg.admin_name;

              return (
                <div
                  key={msg.id}
                  className={`card p-4 ${
                    isAdminMessage
                      ? 'bg-rdc-forest/10 border-rdc-forest/30'
                      : msg.role === 'assistant'
                      ? 'bg-rdc-forest/5 border-rdc-forest/20'
                      : msg.role === 'system'
                      ? 'bg-amber-50 border-amber-200'
                      : 'bg-white'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {msg.role === 'user' ? (
                      <div className="w-6 h-6 rounded-full bg-rdc-primary/10 flex items-center justify-center">
                        <User size={12} className="text-rdc-primary" />
                      </div>
                    ) : msg.role === 'assistant' ? (
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                        isAdminMessage ? 'bg-rdc-forest/20' : 'bg-rdc-forest/10'
                      }`}>
                        {isAdminMessage ? (
                          <Wrench size={12} className="text-rdc-forest" />
                        ) : (
                          <Bot size={12} className="text-rdc-forest" />
                        )}
                      </div>
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center">
                        <Wrench size={12} className="text-amber-600" />
                      </div>
                    )}
                    <span className={`text-xs font-semibold uppercase tracking-wide ${
                      isAdminMessage ? 'text-rdc-forest' :
                      msg.role === 'assistant' ? 'text-rdc-forest' :
                      msg.role === 'system' ? 'text-amber-600' : 'text-rdc-primary'
                    }`}>
                      {isAdminMessage ? `Admin: ${msg.admin_name}` :
                       msg.role === 'assistant' ? 'AI Concierge' :
                       msg.role === 'system' ? 'System' : 'Client'}
                    </span>
                    <span className="text-xs text-rdc-taupe ml-auto">
                      {format(new Date(msg.created_at), 'h:mm a')}
                    </span>
                  </div>
                  <div className="text-sm text-rdc-charcoal leading-relaxed whitespace-pre-wrap pl-8">
                    {msg.content}
                  </div>
                  {msg.tool_calls && (
                    <details className="mt-2 pl-8">
                      <summary className="text-xs text-rdc-taupe cursor-pointer hover:text-rdc-primary">
                        Tool calls
                      </summary>
                      <pre className="mt-1 text-xs bg-rdc-cream p-3 rounded-lg overflow-x-auto text-rdc-charcoal">
                        {JSON.stringify(msg.tool_calls, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* Admin Reply Input */}
        {selectedSession && selectedSession.mode === 'human' && (
          <div className="card p-5 mt-6 bg-rdc-forest/5 border-rdc-forest/20">
            <h3 className="text-sm font-semibold text-rdc-forest mb-3">Send Reply</h3>

            {replyError && (
              <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {replyError}
              </div>
            )}

            <div className="flex gap-3">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type your reply to the customer..."
                rows={3}
                disabled={sendingReply}
                className="flex-1 px-4 py-3 border border-rdc-warm-gray rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-rdc-primary focus:border-transparent disabled:opacity-50 bg-white"
              />
              <button
                onClick={handleSendReply}
                disabled={!replyText.trim() || sendingReply}
                className="px-4 py-3 bg-rdc-forest text-white rounded-lg hover:bg-rdc-forest-dark transition-colors disabled:opacity-50 flex items-center justify-center flex-shrink-0 h-fit"
              >
                {sendingReply ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                ) : (
                  <Send size={18} />
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Session list view
  return (
    <div className="animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="page-title">Concierge Logs</h1>
          <p className="text-rdc-taupe mt-1">{sessions.length} AI chat sessions</p>
        </div>
        <button
          onClick={loadSessions}
          className="btn-primary flex items-center gap-2"
        >
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-rdc-taupe" />
        <input
          type="text"
          placeholder="Search by client name, email, or vehicle..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input-field pl-12"
        />
      </div>

      {filteredSessions.length === 0 ? (
        <div className="card p-12 text-center">
          <MessageSquare size={48} className="text-rdc-warm-gray mx-auto mb-4" />
          <h3 className="font-display text-lg font-semibold text-black mb-2">
            {search ? 'No sessions found' : 'No concierge sessions yet'}
          </h3>
          <p className="text-rdc-taupe">
            {search ? 'Try a different search term' : 'Sessions will appear here when clients use the AI concierge'}
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="hidden md:grid grid-cols-[2fr_2fr_1fr_1fr_50px] gap-4 px-5 py-3 bg-rdc-cream text-xs font-semibold text-rdc-taupe uppercase tracking-wide">
            <div>Client</div>
            <div>Session</div>
            <div>Vehicle</div>
            <div>Last Activity</div>
            <div></div>
          </div>

          <div className="divide-y divide-rdc-cream">
            {filteredSessions.map(session => {
              const client = profiles[session.client_id]
              return (
                <button
                  key={session.id}
                  onClick={() => loadMessages(session)}
                  className="w-full grid grid-cols-1 md:grid-cols-[2fr_2fr_1fr_1fr_50px] gap-2 md:gap-4 p-5 hover:bg-rdc-cream/50 transition-colors items-center text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-rdc-primary/10 flex items-center justify-center font-semibold text-rdc-primary">
                      {client?.full_name?.charAt(0) || '?'}
                    </div>
                    <div className="min-w-0">
                      <span className="font-medium text-black block truncate">{client?.full_name || 'Unknown'}</span>
                      <span className="text-xs text-rdc-taupe block truncate">{client?.email || ''}</span>
                    </div>
                  </div>
                  <div className="text-sm text-rdc-dark-gray truncate">
                    {session.title || 'Untitled Session'}
                  </div>
                  <div className="text-sm text-rdc-taupe flex items-center gap-1">
                    {session.vehicle ? (
                      <>
                        <Car size={14} />
                        <span className="truncate">{session.vehicle.year} {session.vehicle.make}</span>
                      </>
                    ) : (
                      <span className="text-rdc-warm-gray">—</span>
                    )}
                  </div>
                  <div className="text-sm text-rdc-taupe">
                    {format(new Date(session.updated_at), 'MMM d, h:mm a')}
                  </div>
                  <div className="flex justify-end">
                    <ChevronRight size={20} className="text-rdc-warm-gray" />
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
